import {
  ERROR_MESSAGES_TYPES,
  INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA,
  NETWORK_ERROR_MESSAGE_RESPONSE_DATA,
  NO_ACCESS_ERROR_NAME,
} from 'app/ConversationPage/constants'
import { ConversationError } from 'app/ConversationPage/utils/customError'
import { useEventSourceApi } from 'utils/api'
import { SessionError } from 'utils/customError'
import { ForbiddenError } from 'app/ConversationPage/utils/customError'
import { AbortRequestService } from 'app/ConversationPage/utils/api'

function handleError(status, path, error) {
  if (status === 401) {
    throw new SessionError(path, error)
  }
  if (error instanceof ForbiddenError) {
    throw error
  }
}

export type TAPIConversationStreamProps = {
  onMessage?: (msg?: any) => void
  onOpen?: (response?: any) => void
  onClose?: (response?: any) => void
  onError?: (err?: Error) => void
}
export const useAPIConversationStream = ({
  onMessage = () => {},
  onOpen = () => {},
  onClose = () => {},
  onError = () => {},
}: TAPIConversationStreamProps) => {
  const path = '/guru/guru-ai-be/v0/conversation-stream'
  const abortController = AbortRequestService.getInstance()
  const signal = abortController.getSignal()

  return useEventSourceApi(path, 'POST', {
    eventSourceConfig: {
      signal,
      openWhenHidden: true,
    },
    onOpen: (response) => {
      if (response.status >= 500) {
        onError(
          new ConversationError(INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA)
        )
        return
      }

      if ([401].includes(response.status)) {
        handleError(response.status, path, response)
        return
      }

      onOpen?.(response)
    },
    onMessage: (msg) => {
      if (signal.aborted) {
        throw new Error()
      }
      if (msg.event === 'done') {
        abortController.abortRequest()
        return
      }

      if (msg.event === 'error') {
        const parsedData = JSON.parse(msg?.data ?? '{}')
        const { name: fallbackName, message: fallbackMessage } =
          INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA
        const fallbackStatusCode = 500

        // throws Forbidden when user has no access/access revoked
        if (parsedData?.code === NO_ACCESS_ERROR_NAME) {
          throw new ForbiddenError(path, new Error(parsedData?.code))
        }

        throw new ConversationError({
          name: parsedData?.code ?? fallbackName,
          message: parsedData?.message ?? fallbackMessage,
          statusCode: msg?.status ?? fallbackStatusCode,
        })
      }

      onMessage?.(msg)
    },
    onClose: () => {
      abortController.abortRequest()
      onClose()
    },
    onError: (err) => {
      abortController.abortRequest()

      if (
        [
          ERROR_MESSAGES_TYPES.NETWORK_ERROR,
          ERROR_MESSAGES_TYPES.TIMEOUT_ERROR,
        ].includes(err?.message)
      ) {
        onError(new ConversationError(NETWORK_ERROR_MESSAGE_RESPONSE_DATA))
        return
      }

      if (err?.message === ERROR_MESSAGES_TYPES.INTERNAL_SERVER_ERROR) {
        onError(
          new ConversationError(INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA)
        )
        return
      }

      if (err?.name === INTERNAL_SERVER_ERROR_MESSAGE_RESPONSE_DATA.name) {
        // re-throw error to prevent retry
        throw err
        return
      }

      onError(err)
    },
    errorHandlerFn: handleError,
  })
}
